/**
 * RebookDrawerHeader — the clay band at the top of the rebook drawer (BON-146).
 *
 * Matched by hand to the design system's CreateDrawer band rather than using
 * that primitive: it brings a stepper and its own token system, both scoped
 * under `.nb-dashboards`, and the bookings area deliberately sits outside that
 * scope. Same call the refund drawer made, same reason.
 */
import { CalendarClock, X } from 'lucide-react';

export interface RebookDrawerHeaderProps {
  title: string;
  /** Seats being moved — every one of them, always; there is no partial move. */
  ticketsCount: number;
  subline: string;
  onClose: () => void;
  closeDisabled: boolean;
  titleId: string;
}

export function RebookDrawerHeader({
  title,
  ticketsCount,
  subline,
  onClose,
  closeDisabled,
  titleId,
}: RebookDrawerHeaderProps) {
  return (
    <div className="bg-clay-500 px-6 pb-[18px] pt-5 text-white">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <span className="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-white/20">
            <CalendarClock size={18} />
          </span>
          <div className="min-w-0">
            {/* Ticket count, not the confirmation code: imported bookings have
                none and fall back to a raw 36-character UUID that wraps across
                the band. The customer is named in the subline. */}
            <div className="text-[10px] font-extrabold uppercase tracking-[0.12em] opacity-70">
              {ticketsCount} {ticketsCount === 1 ? 'ticket' : 'tickets'}
            </div>
            <h2
              id={titleId}
              className="mt-1.5 font-headline text-[26px] font-normal uppercase leading-[1.02] tracking-[0.01em] text-white"
            >
              {title}
            </h2>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          disabled={closeDisabled}
          aria-label="Close"
          className="flex-shrink-0 rounded-lg border border-white/45 p-1.5 text-white hover:bg-white/15 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <X size={18} />
        </button>
      </div>
      <p className="mt-3.5 text-[13px] leading-snug text-white/95">{subline}</p>
    </div>
  );
}
